/**
 * Startup prompt when global notifications are off (见 23).
 *
 * The ledger and the decision live in the extension host. The workbench only
 * paints the snapshot. Not a module concern, and the card sits on the sidebar
 * column. Desktop looks at the standing do-not-disturb switch and at the OS
 * notification master switch plus this app's authorization, which the main
 * process reads and pushes. Web looks at the site's notification permission,
 * which the page reports. One local calendar day keeps the first successful
 * check; a later open that day does not read again (见 23).
 */

export const NOTIFICATION_PROMPT_CHANGED_EVENT =
  'platform.notificationPrompt.changed';

export const NOTIFICATION_PROMPT_READ_COMMAND =
  'workbench.notification.prompt.read';

export const NOTIFICATION_PROMPT_DISMISS_COMMAND =
  'workbench.notification.prompt.dismiss';

/**
 * Renderer → host, web only. The argument is `Notification.permission`.
 * Desktop ignores it. The OS authorization arrives from the main process.
 */
export const NOTIFICATION_PROMPT_PERMISSION_COMMAND =
  'workbench.notification.prompt.permission';

/**
 * The main window became visible (cold start, tray, restore). The host checks
 * once per local day on this edge (见 23).
 */
export const NOTIFICATION_PROMPT_OPEN_COMMAND =
  'workbench.notification.prompt.open';

/**
 * 「开启」actually turned notifications on. Opening system settings does not
 * count: the card stays up (见 23).
 */
export const NOTIFICATION_PROMPT_RESOLVE_COMMAND =
  'workbench.notification.prompt.resolve';

/** Host storage namespace. One record, not a settings key. */
export const NOTIFICATION_PROMPT_STORAGE_NAMESPACE = 'notification-prompt';

export const NOTIFICATION_PROMPT_STORAGE_KEY = 'record';

export type NotificationPromptSurface = 'popup' | 'banner' | 'none';

export interface NotificationPromptSnapshot {
  readonly surface: NotificationPromptSurface;
  /**
   * Web, and only while the card is up because the site permission is missing.
   * `default` can still ask; `denied` cannot — the browser will not re-prompt.
   */
  readonly webPermission?: 'default' | 'denied';
  /**
   * Desktop, and only while the card is up because the OS will not deliver.
   * `not-determined` can still ask (macOS). `denied` is a master switch or an
   * app authorization that is already off — the app cannot flip either, so
   * 「开启」opens the system notification settings.
   */
  readonly systemBlock?: 'denied' | 'not-determined';
}

/**
 * What the main process read out of the OS notification settings (见 23).
 *
 * `authorization` is this app's own switch. `master` is the system-wide
 * notifications switch. macOS has no separate master, so it reports `absent`
 * and only the authorization decides.
 */
export interface SystemNotificationAccess {
  readonly authorization: 'granted' | 'denied' | 'not-determined';
  readonly master: 'on' | 'off' | 'absent';
}

export function isSystemNotificationAccess(
  value: unknown,
): value is SystemNotificationAccess {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const access = value as SystemNotificationAccess;
  return (
    (access.authorization === 'granted' ||
      access.authorization === 'denied' ||
      access.authorization === 'not-determined') &&
    (access.master === 'on' ||
      access.master === 'off' ||
      access.master === 'absent')
  );
}

/**
 * Why the OS will not deliver a notification, or null when it will.
 *
 * A master switch that is off and an app the user (or the OS) has refused are
 * the same outcome for the prompt: open system settings. Not-yet-asked is the
 * one state that can still raise the system prompt.
 */
export function systemNotificationBlock(
  access: SystemNotificationAccess,
): 'denied' | 'not-determined' | null {
  if (access.master === 'off' || access.authorization === 'denied') {
    return 'denied';
  }
  if (access.authorization === 'not-determined') {
    return 'not-determined';
  }
  return null;
}

/**
 * Whether the OS will deliver, as far as this process knows (见 23).
 *
 * `known: false` is not "off": a failed read, a platform with nothing to
 * read, and a check that has not come back yet leave the chime on the sound
 * setting. Treating those as closed would hide the failed read.
 */
export type OsNotificationDelivery =
  | { readonly known: false }
  | {
      readonly known: true;
      readonly block: 'denied' | 'not-determined' | null;
    };

/**
 * Whether this delivery plays the bundled chime (见 23 声音).
 *
 * The OS toast is silent, so the chime is the only sound. It plays for an OS
 * notification only when the system will actually deliver one. A master
 * switch that is off, an app the user refused, and a permission that was
 * never granted are the same: nothing was shown, so nothing rings. An in-app
 * toast does not consult the OS — that surface was shown inside the app.
 */
export function shouldPlayNotificationChime(input: {
  readonly sound: boolean;
  readonly surface: 'os' | 'in-app';
  readonly os?: OsNotificationDelivery;
}): boolean {
  if (!input.sound) {
    return false;
  }
  if (input.surface === 'in-app') {
    return true;
  }
  const os = input.os ?? { known: false };
  if (!os.known) {
    return true;
  }
  return os.block === null;
}

/** Last OS read the main process can hand the desktop driver. */
export function desktopOsNotificationDelivery(
  access: SystemNotificationAccess | null | 'unreadable' | 'unsupported',
): OsNotificationDelivery {
  if (access === null || access === 'unreadable' || access === 'unsupported') {
    return { known: false };
  }
  return { known: true, block: systemNotificationBlock(access) };
}

export type WebNotificationPermission =
  | 'granted'
  | 'denied'
  | 'default'
  | 'missing';

/** Browser permission at the moment the page would construct `Notification`. */
export function webOsNotificationDelivery(
  permission: WebNotificationPermission,
): OsNotificationDelivery {
  if (permission === 'granted') {
    return { known: true, block: null };
  }
  if (permission === 'default') {
    return { known: true, block: 'not-determined' };
  }
  return { known: true, block: 'denied' };
}

/**
 * The one successful check for a local calendar day (见 23).
 *
 * `dismissed` is cancel. `resolved` is 「开启」succeeding. Either one, or a
 * verdict of `on`, means later opens that same day do not show the card and
 * do not read again.
 */
export interface NotificationPromptRecord {
  /** `YYYY-MM-DD` in the host machine's local timezone. */
  readonly day: string;
  readonly verdict: 'on' | 'off';
  readonly dismissed: boolean;
  readonly resolved: boolean;
  readonly systemBlock?: 'denied' | 'not-determined';
  readonly webPermission?: 'default' | 'denied';
}

/** Local calendar day of `now`, `YYYY-MM-DD`. The host machine's timezone. */
export function localCalendarDay(now: number): string {
  const date = new Date(now);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${String(date.getFullYear())}-${month}-${day}`;
}

export type NotificationPromptOpenPlan = 'show' | 'hide' | 'read';

/**
 * What a main-window open does with the day already on the ledger.
 *
 * A different day, or no successful check yet, reads again. The same day
 * shows the card only when that check found notifications off and the user
 * has neither cancelled nor turned them on.
 */
export function planNotificationPromptOpen(
  today: string,
  adopted: NotificationPromptRecord | undefined,
): NotificationPromptOpenPlan {
  if (!adopted || adopted.day !== today) {
    return 'read';
  }
  if (adopted.verdict === 'off' && !adopted.dismissed && !adopted.resolved) {
    return 'show';
  }
  return 'hide';
}

export function globalNotificationsOff(dnd: unknown): boolean {
  return dnd === true;
}

/**
 * Web prompt signal. Granted means the site may notify, so the card stays down.
 * Anything else — including a missing read — is not treated as off: the host
 * waits until the page reports `default` or `denied`.
 */
export function webNotificationPromptOff(permission: unknown): boolean {
  return permission === 'default' || permission === 'denied';
}

/**
 * The System Notification row jumps to the OS settings page. That jump exists
 * on Windows and macOS, where the shell exposed `openSystemSettings`. Web has
 * no such page, and other desktops are not given the Windows URL.
 */
export function systemNotificationSettingsAvailable(
  openSystemSettings: unknown,
  platform: unknown,
): boolean {
  return (
    typeof openSystemSettings === 'function' &&
    (platform === 'win32' || platform === 'darwin')
  );
}
